// 3rd party
import { Injectable, EventEmitter } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs/Rx';

// models
import { ERoleActionWebview, ContactCustomer } from 'app/shared/models/contact-customer.model';
import { Theme } from 'app/shared/models/theme';

// services
import { AuthService } from 'app/shared/services/auth/auth.service';

@Injectable()
export class AuthGuard implements CanActivate {

  private themeWrapper = document.querySelector('body');

  constructor(private router: Router,
    private authService: AuthService) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {

    let contact: ContactCustomer = JSON.parse(localStorage.getItem('currentContact'));
    if (contact && contact.Identifier) {
      if (state.url === '' || state.url === '/' || !(contact.Domain === route.params['domain'])) {
        this.router.navigate([contact.Domain + '/instruments']);
      }
      return true;
    }
    localStorage.setItem('domainApp', route.params['domain']);
    localStorage.setItem('themeName', 'Default');
    localStorage.setItem('hasSiteLogo', 'false');
    localStorage.setItem('hasLoginLogo', 'false');
    localStorage.setItem('webviewCustomLogin', 'false');
    this.themeWrapper.className = localStorage.getItem('themeName');

    this.authService.getPermissionCustomLogin(route.params['domain']).subscribe(response => {
      let hasPermission = JSON.parse(response).HasPermission.toString();
      localStorage.setItem('webviewCustomLogin', hasPermission);

      if (JSON.parse(response).HasPermission) {
        this.authService.getTheme(route.params['domain']).subscribe(result => {
          let hasColors = result != null;
          localStorage.setItem('hasColors', hasColors.toString());
          if (hasColors) {
            let colors = JSON.parse(result.substring(0, result.length - 1).substring(1));

            localStorage.setItem('themeName', colors.Name);
            this.themeWrapper.className = localStorage.getItem('themeName');

          }
          this.authService.getSiteLogo(route.params['domain']).subscribe(data => {
            let hasLogo = data != null;
            localStorage.setItem('hasSiteLogo', hasLogo.toString());

            if (hasLogo) {
              localStorage.setItem('typelogosite', JSON.parse(data).LogoNameLogin.split('.')[1]);
              localStorage.setItem('logosite', JSON.parse(data).BytesConverted);
            }

            this.authService.getLoginLogo(route.params['domain']).subscribe(data => {
              let hasLogo = data != null;
              localStorage.setItem('hasLoginLogo', hasLogo.toString());

              if (hasLogo) {
                localStorage.setItem('typelogologin', JSON.parse(data).LogoNameLogin.split('.')[1]);
                localStorage.setItem('logologin', JSON.parse(data).BytesConverted);
              }

              return this.router.navigate(['/login'], {queryParams: { redirectTo: state.url } });
            });
          });
        });
      } else {
        return this.router.navigate(['/login'], {queryParams: { redirectTo: state.url } });
      }
    });
  }
}

@Injectable()
export class AuthGuardLogin implements CanActivate {
  currentContact = new EventEmitter<ContactCustomer>();
  showApp = new EventEmitter<boolean>();

  constructor(private router: Router) { }

  canActivate(route: ActivatedRouteSnapshot) {
    if (route.queryParams.session) {
      let dataParams = JSON.parse(route.queryParams.session);

      let contactCustomer = dataParams.currentContact as ContactCustomer;
      let systemConfiguration = dataParams.systemConfiguration;
      let hasAssayAccess = dataParams.hasAssayAccess || false;
      let usePdfJsViewer = dataParams.usePdfJsViewer || false;

      if (systemConfiguration) {
        if (systemConfiguration.InstrumentTagName) {
          localStorage.setItem('instrumentTagName', systemConfiguration.InstrumentTagName);
        } else {
          localStorage.removeItem('instrumentTagName');
        }
        if (systemConfiguration.NewCalibrationTagName) {
          localStorage.setItem('newCalibrationTagName', systemConfiguration.NewCalibrationTagName);
        } else {
          localStorage.removeItem('newCalibrationTagName');
        }
      }
      if (contactCustomer && contactCustomer.Identifier) {
        localStorage.setItem('currentContact', JSON.stringify(contactCustomer));
        localStorage.setItem('token', contactCustomer.Token.access_token);
        localStorage.setItem('preferedLang', contactCustomer.PreferedLanguage);
        localStorage.setItem('lastUser', dataParams.login);

        // Acesso ao modulo de ensaios
        if (hasAssayAccess)
          localStorage.setItem('assayAccess', hasAssayAccess);
        else
          localStorage.removeItem('assayAccess');

        if (usePdfJsViewer)
          localStorage.setItem('usePdfJsViewer', usePdfJsViewer);
        else
          localStorage.removeItem('usePdfJsViewer');

        this.currentContact.emit(contactCustomer);
        this.showApp.emit(true);
      }
    }

    let contact = JSON.parse(localStorage.getItem('currentContact'));
    if (!contact) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/instruments`]);
    return false;
  }
}

@Injectable()
export class AuthGuardInstruments implements CanActivate {
  constructor(private router: Router) { }

  canActivate() {
    let requiredPermissions: ERoleActionWebview[] = [ERoleActionWebview.InstrumentsList];
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;

    if (contact && ContactCustomer.hasPermissions(requiredPermissions, contact.RoleWebView.ActionsPermissions)) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }

}

@Injectable()
export class AuthGuardCalibrations implements CanActivate {
  constructor(private router: Router) { }

  canActivate(route: ActivatedRouteSnapshot) {
    let requiredPermissions: ERoleActionWebview[] = [ERoleActionWebview.CertificatesList];
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;

    if (contact && ContactCustomer.hasPermissions(requiredPermissions, contact.RoleWebView.ActionsPermissions)) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }
}

@Injectable()
export class AuthGuardBudgets implements CanActivate {
  constructor(private router: Router) { }

  canActivate() {
    let requiredPermissions: ERoleActionWebview[] = [ERoleActionWebview.BudgetsList];
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;

    if (contact && ContactCustomer.hasPermissions(requiredPermissions, contact.RoleWebView.ActionsPermissions)) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }
}

@Injectable()
export class AuthGuardStandards implements CanActivate {
  constructor(private router: Router) { }

  canActivate() {
    let requiredPermissions: ERoleActionWebview[] = [ERoleActionWebview.StandardsList];
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;

    if (contact && ContactCustomer.hasPermissions(requiredPermissions, contact.RoleWebView.ActionsPermissions)) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }
}

@Injectable()
export class AuthGuardReports implements CanActivate {
  constructor(private router: Router) { }

  canActivate() {
    let requiredPermissions: ERoleActionWebview[] = [ERoleActionWebview.ReportsList];
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;

    if (contact && ContactCustomer.hasPermissions(requiredPermissions, contact.RoleWebView.ActionsPermissions)) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }
}

@Injectable()
export class AuthGuardLogout implements CanActivate {
  constructor(private auhtService: AuthService) { }

  canActivate() {
    this.auhtService.logoff();
    return true;
  }
}

@Injectable()
export class AuthGuardAssays implements CanActivate {
  constructor(private router: Router) { }

  canActivate() {
    let contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;
    let hasAccessAssays = JSON.parse(localStorage.getItem('assayAccess')) || false;

    if (hasAccessAssays) {
      return true;
    }

    this.router.navigate([`/${contact.Domain}/unauthorized`]);
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/auth/auth.guard.ts